import React, { useCallback, useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import TrackerLayout from '../components/TrackerLayout';
import { useMentorSession } from '../context/TrackerContext';
import { getMentorStudents, getMentorStudentPortions, setMentorStudentPortions } from '../lib/api';
import type { StudentBasic, PortionsData, DayItem, Checkpoint, ProjectStatus } from '../lib/api';

const DIFFICULTY_COLORS: Record<string, string> = { Easy: '#22c55e', Medium: '#F0B429', Hard: '#ef4444' };

// ── Portions Panel (inline, not collapsed) ─────────────────────

const PortionsPanel: React.FC<{ studentId: string; mentorId: string }> = ({ studentId, mentorId }) => {
  const [data, setData]   = useState<PortionsData | null>(null);
  const [loading, setLoading] = useState(true);
  const [checked, setChecked] = useState<Set<number>>(new Set());
  const [saving, setSaving]   = useState(false);
  const [dirty, setDirty]     = useState(false);

  const load = useCallback(() => {
    setLoading(true);
    getMentorStudentPortions(studentId)
      .then(res => { setData(res); setChecked(new Set(res.completedDays)); setDirty(false); })
      .catch(err => toast.error(err.message))
      .finally(() => setLoading(false));
  }, [studentId]);

  useEffect(() => { load(); }, [load]);

  const toggle = (day: number) => {
    setChecked(prev => {
      const next = new Set(prev);
      next.has(day) ? next.delete(day) : next.add(day);
      return next;
    });
    setDirty(true);
  };

  const handleSave = async () => {
    if (!dirty || saving) return;
    setSaving(true);
    try {
      await setMentorStudentPortions(studentId, Array.from(checked), mentorId);
      toast.success('Portions saved successfully!');
      setDirty(false);
    } catch (err: any) {
      toast.error(err.message);
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <div className="py-12 text-center text-gray-400 text-sm">Loading portions…</div>;

  if (!data?.totalDays) {
    return (
      <div className="bg-amber-50 border border-amber-200 rounded-2xl p-6 text-center">
        <p className="text-amber-700 text-sm font-medium">Curriculum not assigned for this student.</p>
        <p className="text-amber-500 text-xs mt-1">Ask the admin or coordinator to assign a curriculum first.</p>
      </div>
    );
  }

  const pct = Math.round((checked.size / data.totalDays) * 100);
  const cpMap: Record<number, Checkpoint> = {};
  (data.projectCheckpoints || []).forEach(cp => { cpMap[cp.afterDay] = cp; });
  const statusMap: Record<number, ProjectStatus> = {};
  (data.projectStatuses || []).forEach(ps => { statusMap[ps.phase] = ps; });

  return (
    <div className="space-y-4">
      {/* Progress summary card */}
      <div className="bg-white rounded-2xl p-5 border border-gray-100 shadow-sm">
        <div className="flex items-end justify-between mb-3">
          <div>
            <p className="text-xs text-gray-400 uppercase tracking-wide font-semibold mb-0.5">{data.curriculumTitle}</p>
            <span className="text-4xl font-black" style={{ color: '#2b2240' }}>{pct}%</span>
          </div>
          <span className="text-sm text-gray-500 pb-1">{checked.size} / {data.totalDays} days</span>
        </div>
        <div className="bg-gray-100 rounded-full h-3 overflow-hidden">
          <div className="h-3 rounded-full transition-all duration-500"
            style={{ width: `${pct}%`, background: 'linear-gradient(90deg,#F0B429,#FF6535)' }} />
        </div>
        {dirty && (
          <p className="text-xs text-amber-600 mt-2 font-medium">Unsaved changes — click Save when done.</p>
        )}
      </div>

      {/* Day checklist */}
      <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
        <div className="px-5 py-3 border-b border-gray-100 flex items-center justify-between">
          <p className="text-sm font-bold" style={{ color: '#2b2240' }}>Day-by-Day Portions</p>
          <span className="text-xs text-gray-400">{checked.size} completed</span>
        </div>
        <div className="max-h-[60vh] overflow-y-auto divide-y divide-gray-50">
          {(data.days || []).map((d: DayItem) => {
            const isChecked = checked.has(d.day);
            const cp = cpMap[d.day];
            const ps = cp ? statusMap[cp.phase] : null;
            return (
              <React.Fragment key={d.day}>
                <label className={`flex items-center gap-3 px-5 py-3 cursor-pointer select-none transition-colors ${isChecked ? 'bg-green-50' : 'hover:bg-gray-50'}`}>
                  <input type="checkbox" checked={isChecked} onChange={() => toggle(d.day)}
                    className="w-4 h-4 accent-green-500 flex-shrink-0" />
                  <span className={`text-xs font-bold w-8 flex-shrink-0 ${isChecked ? 'text-green-600' : 'text-gray-400'}`}>
                    Day {String(d.day).padStart(2, '0')}
                  </span>
                  <span className={`text-sm flex-1 min-w-0 ${isChecked ? 'text-green-800' : 'text-gray-600'}`}>{d.topic}</span>
                  {isChecked && <span className="text-green-500 text-base flex-shrink-0">✓</span>}
                </label>
                {cp && (
                  <div className="px-5 py-2 flex items-center gap-2"
                    style={{ background: ps?.status === 'completed' ? '#f0fdf4' : ps?.status === 'available' ? '#fefce8' : '#f9fafb' }}>
                    <span className="text-sm">{ps?.status === 'completed' ? '✅' : ps?.status === 'available' ? '📋' : '🔒'}</span>
                    <span className="text-xs font-bold flex-1"
                      style={{ color: ps?.status === 'completed' ? '#166534' : ps?.status === 'available' ? '#92400e' : '#9ca3af' }}>
                      Project {cp.phase} — {cp.description}
                    </span>
                    <span className="text-xs px-2 py-0.5 rounded-full font-bold text-white"
                      style={{ background: DIFFICULTY_COLORS[cp.difficulty] || '#6b7280' }}>
                      {cp.difficulty}
                    </span>
                  </div>
                )}
              </React.Fragment>
            );
          })}
        </div>
      </div>

      {/* Save button */}
      <button onClick={handleSave} disabled={!dirty || saving}
        className="w-full py-3 rounded-2xl text-sm font-bold text-white transition-all disabled:opacity-40 disabled:cursor-not-allowed"
        style={{ background: dirty ? 'linear-gradient(135deg,#312741,#5a4a6f)' : '#9ca3af' }}>
        {saving ? 'Saving…' : dirty ? 'Save Portions' : 'No changes to save'}
      </button>
    </div>
  );
};

// ── Main Page ──────────────────────────────────────────────────

const MentorPortions: React.FC = () => {
  const { mentorId } = useMentorSession();
  const [students, setStudents]       = useState<StudentBasic[]>([]);
  const [selectedId, setSelectedId]   = useState('');
  const [fetching, setFetching]       = useState(true);

  useEffect(() => {
    setFetching(true);
    getMentorStudents(mentorId)
      .then(res => { setStudents(res.students); if (res.students.length > 0) setSelectedId(res.students[0].studentId); })
      .catch(err => toast.error(err.message))
      .finally(() => setFetching(false));
  }, [mentorId]);

  const selected = students.find(s => s.studentId === selectedId);

  return (
    <TrackerLayout>
      <div className="max-w-2xl mx-auto">
        {/* Header */}
        <div className="mb-5">
          <h1 className="text-2xl font-bold" style={{ color: '#2b2240' }}>Update Portions</h1>
          <p className="text-gray-500 text-sm mt-0.5">Tick the days completed for each student</p>
        </div>

        {fetching ? (
          <div className="flex items-center justify-center h-40 text-gray-400">Loading students…</div>
        ) : students.length === 0 ? (
          <div className="bg-amber-50 border border-amber-200 rounded-2xl p-8 text-center text-amber-700 text-sm">
            No active students assigned to you.
          </div>
        ) : (
          <>
            {/* Student selector */}
            <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-4 mb-4">
              <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Select Student</label>
              <select
                value={selectedId}
                onChange={e => setSelectedId(e.target.value)}
                className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl text-sm font-medium focus:outline-none focus:border-yellow-400"
                style={{ color: '#2b2240' }}
              >
                {students.map(s => (
                  <option key={s.studentId} value={s.studentId}>
                    {s.studentId} · {s.name}
                  </option>
                ))}
              </select>
              {selected && (
                <p className="text-xs text-gray-400 mt-2 px-1">
                  {selected.name} — ID: {selected.studentId}
                </p>
              )}
            </div>

            {/* Portions panel */}
            {selectedId && <PortionsPanel key={selectedId} studentId={selectedId} mentorId={mentorId} />}
          </>
        )}
      </div>
    </TrackerLayout>
  );
};

export default MentorPortions;
